import React, { useEffect, useState } from 'react'
import { Dialog } from '@e3dc-react/shell/Elements/Dialog'
import { Box, Typography } from '@mui/material'
import { makeStyles } from 'tss-react/mui'
import { ShopAdditionalProductsModelResponse, ShopAdditionalSelectedProductProps } from 'src/Libs/shop/models'
import { ShopMainSkeleton } from 'src/Libs/shop/components/Skeletons'
import { useCustomTranslation } from '@e3dc-react/shell/Hooks/useCustomTranslation'
import TransformerMoreInfoProduct from '../SystemConfiguration/Transformer/TransformerMoreInfoProduct'
import AdditionalProductCard from './AdditionalProductsCard'
import { useShop } from '../../hooks'

export interface AdditionalProductsModalProps {
  open: boolean
  place: number
  mainProductId: string
  mainProductQuantity?: number
  minQtyOfAdditionalProducts: number
  onConfirm: (additionalProducts: ShopAdditionalSelectedProductProps[]) => void | Promise<void>
  onClose: () => void
}

const useStyles = makeStyles()(theme => ({
  cardWrapper: {
    marginTop: theme.spacing(2),
    display: 'grid',
    alignItems: 'strech',
    justifyContent: 'center',
    justifyItems: 'center',
    gap: theme.spacing(4),
    gridAutoRows: '1fr',
    gridTemplateColumns: 'repeat(auto-fill, minmax(270px, 1fr))',
    [theme.breakpoints.down('sm')]: {
      gridTemplateColumns: 'repeat(auto-fit, 270px)',
      justifyContent: 'center',
    },
  },
}))

/*
 * This Component Shows Additional products that must be ordered based on main Product.
 */
const AdditionalProductsModal: React.FC<AdditionalProductsModalProps> = ({
  open,
  place,
  onClose,
  onConfirm,
  mainProductId,
  mainProductQuantity,
  minQtyOfAdditionalProducts,
}): JSX.Element => {
  const [isLoading, setIsLoading] = useState<boolean>(true)
  const { getShopAdditionalProducts } = useShop()
  const { t } = useCustomTranslation()
  const { classes } = useStyles()
  const [productData, setProductData] = useState<ShopAdditionalProductsModelResponse | null>(null)
  const [selectedProducts, setSelectedProducts] = useState<ShopAdditionalSelectedProductProps[]>([])
  const [moreInfoProductId, setMoreInfoProductId] = useState<string | null>(null)
  const desiredProduct = productData?.additionalRequiredProducts.find(product => product.productId === moreInfoProductId) ?? null
  const totalQuantityOfSelectedProducts = selectedProducts.reduce((total, product) => total + product.quantity, 0)
  const canOrder = totalQuantityOfSelectedProducts >= minQtyOfAdditionalProducts
  useEffect(() => {
    getShopAdditionalProducts(mainProductId)
      .then(result => setProductData(result))
      .finally(() => setIsLoading(false))
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const handleConfirm = async (): Promise<void> => {
    const allProducts = [...selectedProducts, { id: mainProductId, quantity: 1 }]

    if (mainProductQuantity) {
      await onConfirm(selectedProducts)
      onClose()
      return
    }
    await onConfirm(allProducts)
    onClose()
  }

  const handleSelectedProduct = (productId: string, quantity: number): void => {
    const existingProductIndex = selectedProducts.findIndex(product => product.id === productId)
    const updatedProducts = [...selectedProducts]

    if (existingProductIndex === -1 && quantity <= 0) return

    if (existingProductIndex !== -1 && quantity > 0) {
      updatedProducts[existingProductIndex].quantity = quantity
      return setSelectedProducts(updatedProducts)
    }

    if (existingProductIndex !== -1 && quantity === 0) {
      updatedProducts.splice(existingProductIndex, 1)
      return setSelectedProducts(updatedProducts)
    }

    if (existingProductIndex === -1 && quantity > 0) {
      updatedProducts.push({ id: productId, quantity: quantity })
      setSelectedProducts(updatedProducts)
    }
  }

  const handleMoreInfoProduct = (productId: string | null): void => {
    setMoreInfoProductId(productId)
  }

  const modalActions = {
    primary: {
      name: t('shop.common.further'),
      disabled: !canOrder,
      onClick: (): void => {
        handleConfirm()
      },
    },
  }

  return (
    <Dialog
      title={moreInfoProductId !== null ? undefined : t('shop.additional-products.label')}
      maxWidth={moreInfoProductId !== null ? 'sm' : 'md'}
      onClose={onClose}
      place={place}
      actions={modalActions}
    >
      <Box>
        {moreInfoProductId === null && (
          <Typography sx={{ my: 1 }} variant="h3" textAlign="left">
            {t('shop.additional-products.condition-text', { value: minQtyOfAdditionalProducts })}
          </Typography>
        )}
        {isLoading ? (
          <Box>
            <ShopMainSkeleton shopSkeletonType="product" />
          </Box>
        ) : (
          <Box>
            {moreInfoProductId === null ? (
              <Box className={classes.cardWrapper}>
                {productData?.additionalRequiredProducts
                  .sort((a, b) => a.position - b.position)
                  .map(product => {
                    return (
                      <AdditionalProductCard
                        key={product.productId}
                        productData={product.product}
                        selectedProducts={selectedProducts}
                        onSelectedProductChange={handleSelectedProduct}
                        onMoreInfoProduct={handleMoreInfoProduct}
                      />
                    )
                  })}
              </Box>
            ) : (
              <Box>
                {desiredProduct && (
                  <TransformerMoreInfoProduct productData={desiredProduct.product} onMoreInfoProductChange={handleMoreInfoProduct} />
                )}
              </Box>
            )}
          </Box>
        )}
      </Box>
    </Dialog>
  )
}

export default AdditionalProductsModal
